import { useState } from "react";
import { Euro } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";

export const EMPTY_PRICE = { min: "", max: "", free: false };
const toCents = (v) => (v === "" || isNaN(parseFloat(v)) ? undefined : Math.round(parseFloat(v) * 100));

export const priceParams = (p) =>
  p.free ? { free_only: true } : {
    ...(toCents(p.min) !== undefined ? { min_price_cents: toCents(p.min) } : {}),
    ...(toCents(p.max) !== undefined ? { max_price_cents: toCents(p.max) } : {}),
  };

const label = (p) => {
  if (p.free) return "Free only";
  if (p.min !== "" && p.max !== "") return `€${p.min}–€${p.max}`;
  if (p.min !== "") return `From €${p.min}`;
  if (p.max !== "") return `Up to €${p.max}`;
  return "Price";
};

const field = "w-full h-9 pl-7 pr-2 rounded-lg bg-[#1E1E1E] border border-[#27272A] text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#3B82F6] disabled:opacity-40";

export const PriceFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const active = value.free || value.min !== "" || value.max !== "";
  const invalid = !draft.free && draft.min !== "" && draft.max !== "" && parseFloat(draft.min) > parseFloat(draft.max);
  const apply = () => { onChange(draft); setOpen(false); };
  const clear = () => { setDraft(EMPTY_PRICE); onChange(EMPTY_PRICE); setOpen(false); };
  const num = (k) => (e) => setDraft({ ...draft, [k]: e.target.value.replace(/[^0-9.]/g, "") });

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(value); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="market-price-trigger"
          className={`h-8 shrink-0 rounded-full border px-3 text-xs flex items-center gap-1.5 transition-colors ${active ? "bg-[#2563EB]/15 border-[#2563EB] text-[#93C5FD]" : "bg-[#1E1E1E] border-[#27272A] text-[#D1D5DB]"}`}>
          <Euro className="h-3.5 w-3.5" /> {label(value)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" data-testid="market-price-panel" className="w-64 bg-[#121212] border-[#27272A] text-[#F3F4F6] space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm">Free only</span>
          <Switch data-testid="market-price-free-switch" checked={draft.free} onCheckedChange={(free) => setDraft({ ...draft, free })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {["min", "max"].map((k) => (
            <label key={k} className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">{k === "min" ? "Min" : "Max"}</span>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[#6B7280]">€</span>
                <input data-testid={`market-price-${k}-input`} inputMode="decimal" value={draft[k]} onChange={num(k)} disabled={draft.free}
                  placeholder={k === "min" ? "0" : "Any"} className={field} onKeyDown={(e) => e.key === "Enter" && !invalid && apply()} />
              </div>
            </label>
          ))}
        </div>
        {invalid && <p data-testid="market-price-error" className="text-[11px] text-[#F87171]">Min can't be higher than max</p>}
        <div className="flex justify-between gap-2">
          <button type="button" data-testid="market-price-clear-btn" onClick={clear} className="h-8 px-3 rounded-full text-xs text-[#9CA3AF] hover:text-white hover:bg-[#1E1E1E]">Clear</button>
          <button type="button" data-testid="market-price-apply-btn" onClick={apply} disabled={invalid}
            className="h-8 px-4 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-medium disabled:opacity-40">Apply</button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
